// @ts-check

/**
 * @file Renders a timelogs dashboard metric card.
 */

import Text from "@kaspernj/api-maker/build/utils/text"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"

import Card from "../../../components/card"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TimelogsDashboardSummaryCardProps
 * @property {string} accentColor Color used for the supporting text.
 * @property {string} supportingText Secondary context for the metric.
 * @property {string} testID Base test id for the card.
 * @property {string} title Metric label.
 * @property {string} value Formatted metric value.
 */

/** @typedef {Record<string, never>} TimelogsDashboardSummaryCardState */

/**
 * Displays one dashboard metric with supporting context.
 * @augments {ShapeComponent<TimelogsDashboardSummaryCardProps, TimelogsDashboardSummaryCardState>}
 */
class TimelogsDashboardSummaryCard extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active dashboard theme into the card. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the metric card.
   * @returns {ReactNode} Summary card content.
   */
  render() {
    const {accentColor, supportingText, testID, title, value} = this.p
    const colors = this.tt.colors
    const themeName = this.tt.themeName

    return (
      <Card
        style={styles.card ||= {
          flexGrow: 1,
          gap: 10,
          minWidth: 220,
          padding: 20
        }}
        testID={testID}
      >
        <Text
          style={styles[`title-${themeName}`] ||= {
            color: colors.textMuted,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase"
          }}
          testID={`${testID}/title`}
        >
          {title}
        </Text>
        <Text
          style={styles[`value-${themeName}`] ||= {
            color: colors.text,
            fontSize: 30,
            fontWeight: 700
          }}
          testID={`${testID}/value`}
        >
          {value}
        </Text>
        <Text
          style={styles[`supportingText-${themeName}-${accentColor}`] ||= {
            color: accentColor,
            fontSize: 13,
            fontWeight: 600
          }}
          testID={`${testID}/supportingText`}
        >
          {supportingText}
        </Text>
      </Card>
    )
  }
}

/** @type {NamedExoticComponent<TimelogsDashboardSummaryCardProps>} */
const TimelogsDashboardSummaryCardComponent = memo(shapeComponent(TimelogsDashboardSummaryCard))

export default TimelogsDashboardSummaryCardComponent
